import React, {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useReducer,
} from 'react';

import { initialState } from './constants';
import type { OpenParams, Panel, PreviewPanelsAction, PreviewPanelsApiState } from './types';

const noop = (props: any) => {};

export const PreviewPanelsContext = createContext<
	| {
			state: PreviewPanelsApiState;
			open: (params: OpenParams) => void;
			close: (onClose?: () => void) => void;
			setPanelWidth: (width: number) => void;
	  }
	| undefined
>(undefined);

const previewPanelsReducer = (
	state: PreviewPanelsApiState,
	action: PreviewPanelsAction,
): PreviewPanelsApiState => {
	switch (action.type) {
		case 'SET_PANEL_WIDTH':
			return { ...state, width: action.width };
		case 'CLOSE':
			// Action explicitly called in order to close the active panel.
			action.onClose?.();
			return {
				...initialState,
				width: state.width,
			};
		case 'OPEN':
			// Action explicitly called in order to open a new panel.
			const { params } = action;

			// If the panel is already open, do nothing.
			if (state.panel?.id === params.panel.id) {
				return state;
			}

			// Close the active panel.
			state.onClose();

			// Open the new panel.
			params.onOpen?.();
			return {
				...state,
				panel: params.panel,
				width: params.width ?? state.width,
				onOpen: params.onOpen || initialState.onOpen,
				onClose: params.onClose || initialState.onClose,
			};
		default:
			return state;
	}
};

export const PreviewPanelsProvider = ({
	onPreviewPanelOpen,
	onPreviewPanelClose,
	children,
}: {
	children: ReactNode;
	onPreviewPanelOpen?: () => void;
	onPreviewPanelClose?: () => void;
}) => {
	const [state, dispatch] = useReducer(previewPanelsReducer, initialState);

	const setPanelWidth = useCallback(
		(width: number) => {
			dispatch?.({ type: 'SET_PANEL_WIDTH', width });
		},
		[dispatch],
	);

	const close = useCallback(
		(onClose?: () => void) => {
			dispatch?.({ type: 'CLOSE', onClose });
			onPreviewPanelClose?.();
		},
		[dispatch, onPreviewPanelClose],
	);

	const open = useCallback(
		(params: OpenParams) => {
			dispatch?.({ type: 'OPEN', params });
			onPreviewPanelClose?.();
			onPreviewPanelOpen?.();
		},
		[dispatch, onPreviewPanelClose, onPreviewPanelOpen],
	);

	const context = useMemo(
		() => ({
			state,
			open,
			close,
			setPanelWidth,
		}),
		[state, open, close, setPanelWidth],
	);

	return <PreviewPanelsContext.Provider value={context}>{children}</PreviewPanelsContext.Provider>;
};

// Hook to use the context
export const usePreviewPanelsContext = () => {
	const context = useContext(PreviewPanelsContext);
	return context;
};

// Context selectors
export const useIsPreviewPanelOpen = (panelId?: string): boolean => {
	const context = usePreviewPanelsContext();
	return panelId ? context?.state.panel?.id === panelId : context?.state.panel != null;
};

export const usePreviewPanelsActions = () => {
	const context = usePreviewPanelsContext();
	if (!context) {
		return {
			open: noop,
			close: noop,
			setPanelWidth: noop,
		};
	}

	return {
		open: context.open,
		close: context.close,
		setPanelWidth: context.setPanelWidth,
	};
};

export const useCurrentPreviewPanel = ():
	| (Panel & { width?: number; setWidth?: (width: number) => void })
	| null => {
	const context = usePreviewPanelsContext();

	if (context?.state.panel == null) {
		return null;
	}

	return {
		...context.state.panel,
		width: context.state.width,
		setWidth: context.setPanelWidth,
	};
};
